import React, { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";


const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }
];
const CA_PROVINCES = [
  { value: "AB", label: "Alberta" }, { value: "BC", label: "British Columbia" }, { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" }, { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" }, { value: "ON", label: "Ontario" }, { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" }, { value: "SK", label: "Saskatchewan" }, { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" }, { value: "YT", label: "Yukon" }
];
const COUNTRY_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" }
];

function validatePhone(phone) {
  return /^\+1\d{10}$/.test(phone);
}

function CustomerRegistration({ code }) {
  const [companyConfig, setCompanyConfig] = useState(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    phone: "",
    country: COUNTRY_OPTIONS[0],
    state: null,
  });
  const [stateOptions, setStateOptions] = useState(US_STATES);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const recaptchaRef = useRef();
  
  // Check if ReCAPTCHA is configured
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  console.log('ReCAPTCHA Site Key:', recaptchaSiteKey); // Debug log
  const isRecaptchaEnabled = recaptchaSiteKey && 
    recaptchaSiteKey !== 'your_recaptcha_site_key_here' && 
    recaptchaSiteKey.length > 10; // Valid keys are much longer
  console.log('ReCAPTCHA Enabled:', isRecaptchaEnabled); // Debug log

  // Load company config from JSON
  useEffect(() => {
    fetch('/config.json')
      .then(res => res.json())
      .then(data => setCompanyConfig(data))
      .catch(err => {
        console.error('Failed to load config:', err);
        // Set default config if fetch fails
        setCompanyConfig({
          companies: {},
          default: { shortName: 'Amsense', longName: 'Amsense Services' }
        });
      });
  }, []);

  // Get content based on code and config
  const getContent = () => {
    if (!companyConfig) return null;
    
    const mapping = companyConfig.companies[code?.toLowerCase()] || companyConfig.default;
    
    return {
      title: `${mapping.shortName} Customer Registration`,
      subtitle: `Register below to receive SMS updates about your service request. Your phone number will be used only to send important updates and job status notifications from ${mapping.longName}, the service provider handling your request. No marketing messages will be sent. Reply STOP to unsubscribe or HELP for assistance.`,
      serviceProvider: mapping.shortName
    };
  };

  const content = getContent();

  useEffect(() => {
    if (form.country.value === "US") setStateOptions(US_STATES);
    else setStateOptions(CA_PROVINCES);
    setForm(f => ({ ...f, state: null }));
    // eslint-disable-next-line
  }, [form.country]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (
      !form.firstName ||
      !form.lastName ||
      !form.address1 ||
      !form.city ||
      !form.phone ||
      !form.country ||
      !form.state
    ) {
      setError("All fields are required.");
      return;
    }

    if (!consent) {
      setError("You must agree to receive text messages to register.");
      return;
    }

    if (isRecaptchaEnabled && !captchaToken) {
      setError("Please complete the CAPTCHA.");
      return;
    }

    // Format phone to E.164
    let phone = form.phone.trim();
    if (!phone.startsWith("+1")) {
      phone = "+1" + phone.replace(/\D/g, "");
    }
    if (!validatePhone(phone)) {
      setError("Please enter a valid US or Canadian phone number in the format +1XXXXXXXXXX.");
      return;
    }

    // --- Call backend customer_service ---
    try {
      const res = await fetch("https://api.coplaid.ai/customers/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          phone_number: phone,
          country: form.country.value,
          state: form.state.value,
          sms_agreed: consent,
          captcha: captchaToken || null,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Registration failed.");
        if (isRecaptchaEnabled && recaptchaRef.current) recaptchaRef.current.reset();
        setCaptchaToken("");
        return;
      }

      setSuccess("Thank you for registering! You will receive SMS updates soon.");
      setForm({
        firstName: "",
        lastName: "",
        address1: "",
        address2: "",
        city: "",
        phone: "",
        country: COUNTRY_OPTIONS[0],
        state: null,
        accountId: form.accountId, // preserve accountId if needed
      });
      setConsent(false);
      setCaptchaToken("");
      if (isRecaptchaEnabled && recaptchaRef.current) recaptchaRef.current.reset();
    } catch (err) {
      setError("Registration failed. Please try again.");
      if (isRecaptchaEnabled && recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaToken("");
    }
  };

  const labelStyle = { 
    display: "block", 
    marginBottom: 6, 
    fontWeight: 600, 
    color: "#6665dd",
    fontSize: "0.95rem"
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "2px solid #b3b3f7",
    borderRadius: "8px",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    boxSizing: "border-box"
  };

  const inputFocusStyle = {
    outline: "none",
    borderColor: "#6665dd",
    boxShadow: "0 0 0 3px rgba(102, 101, 221, 0.1)"
  };

  const containerStyle = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #b3b3f7 0%, #6665dd 100%)",
    padding: "40px 20px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif"
  };

  const formContainerStyle = {
    maxWidth: 700,
    margin: "0 auto",
    padding: 32,
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(102, 101, 221, 0.15)",
    border: "2px solid #6665dd"
  };

  const titleStyle = {
    textAlign: "center",
    color: "#6665dd",
    fontSize: "2.2rem",
    fontWeight: "700",
    marginBottom: "10px"
  };

  const subtitleStyle = {
    textAlign: "center",
    marginBottom: 32,
    color: "#555",
    fontSize: "1.1rem",
    lineHeight: "1.5"
  };

  // Show loading state while config loads
  if (!content) {
    return (
      <div style={containerStyle}>
        <div style={formContainerStyle}>
          <p style={{ textAlign: 'center', color: '#6665dd' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={formContainerStyle}>
        {code && (
          <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '0.9rem', color: '#666' }}>
            Registration Code: <b style={{ color: '#6665dd' }}>{code}</b>
          </div>
        )}
        <h2 style={titleStyle}>{content.title}</h2>
        <p style={subtitleStyle}>
          {content.subtitle}
        </p>
      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
      {success && <p style={{ color: "green", textAlign: "center" }}>{success}</p>}
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            alignItems: "start",
          }}
        >
          {/* Row 1 */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="firstName">First Name *</label>
              <input 
                type="text" 
                id="firstName" 
                name="firstName" 
                value={form.firstName} 
                onChange={handleChange} 
                required 
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              />
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="lastName">Last Name *</label>
              <input 
                type="text" 
                id="lastName" 
                name="lastName" 
                value={form.lastName} 
                onChange={handleChange} 
                required 
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              />
            </div>
          </div>
          {/* Row 2 */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="address1">Address 1 *</label>
              <input 
                type="text" 
                id="address1" 
                name="address1" 
                value={form.address1} 
                onChange={handleChange} 
                required 
                placeholder="Street address" 
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              />
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="address2">Address 2</label>
              <input 
                type="text" 
                id="address2" 
                name="address2" 
                value={form.address2} 
                onChange={handleChange} 
                placeholder="Apt, suite, unit, etc. (optional)" 
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              />
            </div>
          </div>
          {/* Row 3 */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="city">City *</label>
              <input 
                type="text" 
                id="city" 
                name="city" 
                value={form.city} 
                onChange={handleChange} 
                required 
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              />
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                placeholder="e.g. 5551234567"
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              />
              <br></br><small style={{ color: "#666", fontSize: "0.85rem" }}>Enter your 10-digit US/Canada number. It will be formatted as +1XXXXXXXXXX.</small>
            </div>
          </div>
          {/* Row 4 */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="country">Country *</label>
              <select
                id="country"
                name="country"
                value={form.country.value}
                onChange={e => {
                  const selected = COUNTRY_OPTIONS.find(c => c.value === e.target.value);
                  handleSelectChange("country", selected);
                }}
                required
                style={{...inputStyle, cursor: "pointer"}}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, {...inputStyle, cursor: "pointer"})}
              >
                {COUNTRY_OPTIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle} htmlFor="state">State/Province *</label>
              <select
                id="state"
                name="state"
                value={form.state ? form.state.value : ""}
                onChange={e => {
                  const selected = stateOptions.find(s => s.value === e.target.value);
                  handleSelectChange("state", selected);
                }}
                required
                style={{...inputStyle, cursor: "pointer"}}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => Object.assign(e.target.style, {...inputStyle, cursor: "pointer"})}
              >
                <option value="">Select state/province</option>
                {stateOptions.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Consent */}
         <div style={{ gridColumn: "1 / span 2", marginBottom: 20 }}>
            <label style={{ 
              fontSize: "0.95em", 
              display: "flex", 
              alignItems: "flex-start",
              lineHeight: "1.5",
              color: "#555"
            }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
                style={{ 
                  marginRight: 12,
                  marginTop: 2,
                  transform: "scale(1.2)",
                  accentColor: "#6665dd"
                }}
                required
              />
              <span>
                I consent to receive automated text messages regarding my service request, including status updates and appointment notifications, sent by <b style={{ color: "#6665dd" }}>{content.serviceProvider}</b> on behalf of my service provider. Message and data rates may apply. Consent is not a condition of purchase.
              </span>
            </label>
          </div>
          {/* CAPTCHA */}
          {isRecaptchaEnabled ? (
            <div style={{ gridColumn: "1 / span 2", marginBottom: 16, textAlign: "center" }}>
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                size="normal"
                ref={recaptchaRef}
                onChange={token => setCaptchaToken(token)}
              />
            </div>
          ) : (
            <div style={{ gridColumn: "1 / span 2", marginBottom: 16, textAlign: "center" }}>
              <p style={{ 
                color: "#666", 
                fontSize: "0.9em", 
                fontStyle: "italic", 
                background: "#f8f9fa", 
                padding: "10px", 
                borderRadius: "4px",
                border: "1px solid #e0e0e0"
              }}>
                ReCAPTCHA is not configured. For production use, please set REACT_APP_RECAPTCHA_SITE_KEY.
              </p>
            </div>
          )}
        </div>
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <button 
            type="submit" 
            style={{ 
              background: "linear-gradient(135deg, #b3b3f7 0%, #9b9af2 100%)",
              color: "#fff",
              border: "none",
              padding: "16px 32px",
              fontSize: "18px",
              fontWeight: "600",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(179, 179, 247, 0.3)"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 20px rgba(179, 179, 247, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(179, 179, 247, 0.3)";
            }}
          >
            Register
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default CustomerRegistration;